// apps/alivia/diagnostic.js — DG·1/DG·2: pure diagnostic arithmetic (client-bundled + tested).
// The official guide's 100 sample questions are College Board copyright and are NEVER transcribed
// into the app — she sits them on paper; only the RESULT (per-domain attempted/missed) enters as
// a dgn_ record. These helpers turn that result into honest priorities.

/** Per-domain accuracy from a diagnostic's perDomain map. Domains with attempted 0 are omitted. */
export function accuracyOf(perDomain) {
  const out = {};
  for (const [tag, r] of Object.entries(perDomain ?? {})) {
    const attempted = Number(r?.attempted) || 0;
    const missed = Number(r?.missed) || 0;
    if (attempted > 0) out[tag] = Math.max(0, Math.min(1, (attempted - missed) / attempted));
  }
  return out;
}

/**
 * "Where to spend your hours": weight × miss-rate, so a HEAVY domain she is weak in outranks a
 * light domain she is weaker in. Domains without diagnostic data are listed after the ranked
 * ones (unknown ≠ fine), flagged `noData`. The `line` field is the visible arithmetic — one line,
 * so she can see WHY the order is what it is.
 * @param {Record<string, number>} blueprint  tag -> weight (sums to 100)
 * @param {Record<string, number>} accuracy   tag -> 0..1 (from accuracyOf)
 * @returns {Array<{tag: string, weight: number, accuracy: number|null, priority: number|null, noData?: true, line: string}>}
 */
export function spendRanking(blueprint, accuracy) {
  const ranked = [];
  const unknown = [];
  for (const [tag, weight] of Object.entries(blueprint ?? {})) {
    const acc = accuracy?.[tag];
    if (typeof acc === "number") {
      const missRate = 1 - acc;
      const priority = weight * missRate;
      ranked.push({
        tag, weight, accuracy: acc, priority,
        line: `${weight.toFixed(1)}% of the exam × ${Math.round(missRate * 100)}% missed = ${priority.toFixed(1)}`,
      });
    } else {
      unknown.push({ tag, weight, accuracy: null, priority: null, noData: true, line: `${weight.toFixed(1)}% of the exam × no diagnostic yet` });
    }
  }
  ranked.sort((a, b) => b.priority - a.priority || b.weight - a.weight || a.tag.localeCompare(b.tag));
  unknown.sort((a, b) => b.weight - a.weight || a.tag.localeCompare(b.tag));
  return [...ranked, ...unknown];
}
